import { JanusAPIv2Client } from '../client';
import { 
    AiProviderDto, 
    Options,
    OpenAIOptions,
    GeminiOptions,
    AnthropicOptions,
    BaseRequestOptions
} from '../types';
import { parseModelAndOptions } from '../utils';

export class AiClient {
  constructor(private client: JanusAPIv2Client) {}

  /**
   * Get all available AI providers and their supported models.
   * @returns Promise resolving to an array of available AI providers.
   */
  async getProviders(): Promise<AiProviderDto[]> {
    const response = await this.client._request<{ providers: AiProviderDto[] }>(
      'GET', 
      'ai/providers'
    );
    return response.providers || [];
  }

  // OpenAI method overloads for better type safety
  async openai<TResponse = any>(
    messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>,
    model?: string
  ): Promise<TResponse>;
  async openai<TResponse = any>(
    messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>,
    options: OpenAIOptions
  ): Promise<TResponse>;
  async openai<TResponse = any>(
    messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>,
    model: string,
    options: OpenAIOptions
  ): Promise<TResponse>;

  /**
   * Send a chat completion request to OpenAI via proxy.
   * @param messages Array of chat messages
   * @param model OpenAI model to use or options object (default: gpt-4.1-mini)
   * @param options Additional OpenAI parameters and request options
   */
  async openai<TResponse = any>(
    messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>,
    modelOrOptions: string | OpenAIOptions = 'gpt-4.1-mini',
    options?: OpenAIOptions
  ): Promise<TResponse> {
    // Parameter validation
    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      throw new Error('Messages array is required and cannot be empty');
    }

    const { model, finalOptions } = parseModelAndOptions(
      'gpt-4.1-mini',
      modelOrOptions,
      options
    );

    // Restore validation for the options object to catch invalid keys
    if (typeof modelOrOptions === 'object' && finalOptions && typeof finalOptions === 'object') {
      const validKeys = ['max_tokens', 'temperature', 'stream', 'headers', 'params', 'responseType', 'skipContentTypeHeader'];
      const invalidKeys = Object.keys(finalOptions).filter(key => !validKeys.includes(key) && !key.startsWith('_'));
      if (invalidKeys.length > 0 && !finalOptions.hasOwnProperty('max_tokens') && !finalOptions.hasOwnProperty('temperature')) {
        throw new Error(`Invalid options object. Unexpected keys: ${invalidKeys.join(', ')}`);
      }
    }

    const { max_tokens, temperature, stream, ...requestOptions } = finalOptions;
    
    const body = {
      model,
      messages,
      ...(max_tokens && { max_tokens }),
      ...(temperature !== undefined && { temperature }),
      ...(stream !== undefined && { stream }),
    };

    return this.client._request<TResponse>('POST', 'ai/openai/v1/chat/completions', {
      ...requestOptions,
      body,
    });
  }

  // Gemini method overloads
  async gemini<TResponse = any>(
    prompt: string,
    model?: string
  ): Promise<TResponse>;
  async gemini<TResponse = any>(
    prompt: string,
    options: GeminiOptions
  ): Promise<TResponse>;
  async gemini<TResponse = any>(
    prompt: string,
    model: string,
    options: GeminiOptions
  ): Promise<TResponse>;

  /**
   * Send a request to Gemini via proxy.
   * @param prompt Text prompt for generation
   * @param model Gemini model to use or options object (default: gemini-2.0-flash)
   * @param options Additional Gemini parameters and request options
   */
  async gemini<TResponse = any>(
    prompt: string,
    modelOrOptions: string | GeminiOptions = 'gemini-2.0-flash',
    options?: GeminiOptions
  ): Promise<TResponse> {
    // Parameter validation
    if (!prompt || typeof prompt !== 'string') {
      throw new Error('Prompt is required and must be a non-empty string');
    }

    const { model, finalOptions } = parseModelAndOptions(
      'gemini-2.0-flash',
      modelOrOptions,
      options
    );

    // Extract known options, but be permissive with unknown ones for backward compatibility
    const { generationConfig, safetySettings, stream, ...requestOptions } = finalOptions;
    
    // Handle legacy temperature parameter by wrapping it in generationConfig
    if ('temperature' in finalOptions && !generationConfig) {
      const legacyTemp = (finalOptions as any).temperature;
      const body = {
        contents: [{ parts: [{ text: prompt }] }],
        generationConfig: { temperature: legacyTemp },
        ...(safetySettings && { safetySettings }),
      };
      
      const action = stream ? ':streamGenerateContent' : ':generateContent';
      const path = `ai/gemini/v1beta/models/${model}${action}`;

      return this.client._request<TResponse>('POST', path, {
        ...requestOptions,
        body,
      });
    }
    
    const body = {
      contents: [{ parts: [{ text: prompt }] }],
      ...(generationConfig && { generationConfig }),
      ...(safetySettings && { safetySettings }),
    };

    const action = stream ? ':streamGenerateContent' : ':generateContent';
    const path = `ai/gemini/v1beta/models/${model}${action}`;

    return this.client._request<TResponse>('POST', path, {
      ...requestOptions,
      body,
    });
  }

  // Anthropic method overloads
  async anthropic<TResponse = any>(
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    model?: string
  ): Promise<TResponse>;
  async anthropic<TResponse = any>(
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    options: AnthropicOptions
  ): Promise<TResponse>;
  async anthropic<TResponse = any>(
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    model: string,
    options: AnthropicOptions
  ): Promise<TResponse>;

  /**
   * Send a request to Anthropic Claude via proxy.
   * @param messages Array of chat messages
   * @param model Claude model to use or options object (default: claude-3-haiku-20240307)
   * @param options Additional Claude parameters and request options
   */
  async anthropic<TResponse = any>(
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
    modelOrOptions: string | AnthropicOptions = 'claude-3-haiku-20240307',
    options?: AnthropicOptions
  ): Promise<TResponse> {
    // Parameter validation
    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      throw new Error('Messages array is required and cannot be empty');
    }

    const { model, finalOptions } = parseModelAndOptions(
      'claude-3-haiku-20240307',
      modelOrOptions,
      options
    );

    const { max_tokens = 1024, temperature, stream, system, ...requestOptions } = finalOptions;
    
    const body = {
      model,
      messages,
      max_tokens,
      ...(temperature !== undefined && { temperature }),
      ...(stream !== undefined && { stream }),
      ...(system && { system }),
    };

    return this.client._request<TResponse>('POST', 'ai/anthropic/v1/messages', {
      ...requestOptions,
      body,
    });
  }

  // Proxy method overloads
  async proxy<TResponse = any>(
    provider: string,
    path: string,
    body?: any,
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  ): Promise<TResponse>;
  async proxy<TResponse = any>(
    provider: string,
    path: string,
    body: any,
    options: BaseRequestOptions
  ): Promise<TResponse>;
  async proxy<TResponse = any>(
    provider: string,
    path: string,
    body: any,
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    options: BaseRequestOptions
  ): Promise<TResponse>;

  /**
   * Generic AI proxy method for custom provider integrations.
   * @param provider Provider name (e.g., 'openai', 'gemini', 'anthropic')
   * @param path API path within the provider (e.g., 'v1/chat/completions')
   * @param body Request body
   * @param method HTTP method or options object (default: POST)
   * @param options Additional request options
   */
  async proxy<TResponse = any>(
    provider: string,
    path: string,
    body?: any,
    methodOrOptions: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | BaseRequestOptions = 'POST',
    options?: BaseRequestOptions
  ): Promise<TResponse> {
    // Parameter validation
    if (!provider || typeof provider !== 'string') {
      throw new Error('Provider name is required and must be a string');
    }
    if (!path || typeof path !== 'string') {
      throw new Error('Path is required and must be a string');
    }

    const fullPath = `ai/${provider}/${path}`;
    
    let method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    let finalOptions: BaseRequestOptions;

    // Handle parameter overloads
    if (typeof methodOrOptions === 'string') {
      method = methodOrOptions;
      finalOptions = options || {};
    } else {
      method = 'POST';
      finalOptions = methodOrOptions || {};
    }

    const requestOptions: BaseRequestOptions = {
      ...finalOptions,
      body: method !== 'GET' ? body : undefined,
      params: method === 'GET' ? body : finalOptions.params,
    };

    return this.client._request<TResponse>(method, fullPath, requestOptions);
  }
} 